import { useEffect, useMemo, useState } from 'react';
import { config } from '@/config/env';
import { EmbeddingContainer } from './EmbeddingContainer';
import { useAuthorization } from '@/features/auth/hooks';
import type { EmbeddedService } from '@/types/embedding';
import {
  buildIframeUrlForShellLocation,
  findIframeServiceForShellPath,
  shouldPreserveIframeUrlForEmbeddedNavigation,
} from '../utils/routes';

interface PersistentIframeRoutesProps {
  activePath: string;
  activeSearch?: string;
  activeHash?: string;
  embeddedNavigationServiceId?: string | null;
  navigationType?: string;
}

export function PersistentIframeRoutes({
  activePath,
  activeSearch = '',
  activeHash = '',
  embeddedNavigationServiceId = null,
  navigationType,
}: PersistentIframeRoutesProps) {
  const { filterServices } = useAuthorization();
  const iframeServices = useMemo(
    () => filterServices(config().EMBEDDED_SERVICES || []).filter((service) => service.type === 'iframe'),
    [filterServices],
  );
  const activeService = findIframeServiceForShellPath(iframeServices, activePath);
  const activeServiceId = activeService?.id ?? null;
  const activeServiceUrl = activeService
    ? buildIframeUrlForShellLocation(activeService, {
        pathname: activePath,
        search: activeSearch,
        hash: activeHash,
      })
    : null;
  const [loadedServices, setLoadedServices] = useState<Record<string, EmbeddedService>>({});

  useEffect(() => {
    if (!activeService || !activeServiceUrl) {
      return;
    }

    setLoadedServices((current) => {
      const currentService = current[activeService.id];
      const preserveIframeUrl = shouldPreserveIframeUrlForEmbeddedNavigation(
        activeService.id,
        embeddedNavigationServiceId,
        navigationType,
      );

      if (currentService && preserveIframeUrl) {
        return current;
      }

      if (currentService?.url === activeServiceUrl) {
        return current;
      }

      return {
        ...current,
        [activeService.id]: {
          ...activeService,
          url: activeServiceUrl,
        },
      };
    });
  }, [activeService, activeServiceUrl, embeddedNavigationServiceId, navigationType]);

  const renderedServices = iframeServices.flatMap((service) => {
    const loadedService = loadedServices[service.id];
    if (loadedService) {
      return [loadedService];
    }

    if (service.id === activeServiceId && activeServiceUrl) {
      return [{ ...service, url: activeServiceUrl }];
    }

    return [];
  });

  if (renderedServices.length === 0) {
    return null;
  }

  return (
    <section
      className="community-shell-screen"
      hidden={!activeService}
      aria-hidden={!activeService}
    >
      {renderedServices.map((service) => {
        const isActive = service.id === activeServiceId;

        return (
          <div
            key={service.id}
            data-testid={`persistent-iframe-route-${service.id}`}
            hidden={!isActive}
            aria-hidden={!isActive}
            style={{ height: '100%' }}
          >
            <EmbeddingContainer service={service} className="community-frame-wrap" />
          </div>
        );
      })}
    </section>
  );
}

export default PersistentIframeRoutes;
